import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSettings } from '../App';
import Icon from '../components/Icon';
import './PageContrat.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const MOIS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str + 'T12:00:00');
  return `${d.getDate()} ${MOIS[d.getMonth()]} ${d.getFullYear()}`;
}

function fmtMontant(n) {
  if (n == null) return '—';
  return `${Number(n).toFixed(2)} $`;
}

function fmtDatetime(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('fr-CA', { dateStyle: 'medium', timeStyle: 'short' });
}

const STATUT_LABELS = {
  brouillon: 'Brouillon', en_construction: 'En cours de préparation',
  envoye: 'Prête pour signature', en_revision: 'Modifications en cours',
  accepte: 'Acceptée et confirmée', refuse: 'Refusée',
  annule: 'Annulée', complete: 'Événement terminé',
};

export default function PageContrat() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const settings = useSettings();
  const [contrat,  setContrat]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [erreur,   setErreur]   = useState('');
  const [onglet,   setOnglet]   = useState('soumission');
  const [msg,      setMsg]      = useState('');

  // Commentaires
  const [msgTexte, setMsgTexte] = useState('');
  const [auteurNom,setAuteurNom]= useState('');
  const [envoi,    setEnvoi]    = useState(false);

  // Signature / acceptation
  const [signeNom,  setSigneNom]  = useState('');
  const [signeTitre,setSigneTitre]= useState('');
  const [termsLus,  setTermsLus]  = useState(false);
  const [signEnvoi, setSignEnvoi] = useState(false);
  const [signed,    setSigned]    = useState(false);

  // Refus
  const [showRefus,  setShowRefus]  = useState(false);
  const [raisonRefus,setRaisonRefus]= useState('');

  // Paiement
  const [payEnvoi, setPayEnvoi] = useState(false);

  // Page d'accueil / landing
  const [showLanding, setShowLanding] = useState(true);

  // Choix client menus (type='choix')
  const [choixMenus, setChoixMenus] = useState({});  // { [section_id]: { [item_id]: quantite } }
  const [savingChoix, setSavingChoix] = useState(false);

  // Toggle détail menu dans la soumission
  const [expandedMenuItems, setExpandedMenuItems] = useState(new Set());
  const [menuDetailsCache,  setMenuDetailsCache]  = useState({});

  // Menus complets (avec sections + items) pour l'onglet Menu
  const [menusComplets, setMenusComplets] = useState(null); // null = pas encore chargé
  const [menusErreur,   setMenusErreur]   = useState('');

  useEffect(() => {
    charger();
    // Message de confirmation Stripe
    const paid = searchParams.get('paid');
    if (paid) {
      showFlash(`Paiement ${paid === 'acompte' ? 'de l\'acompte' : 'du solde'} reçu avec succès ✓`, 'success');
    }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (onglet === 'menu' && menusComplets === null && token) {
      fetch(`${API}/api/public/contrat/${token}/menus`)
        .then(async r => {
          const data = await r.json();
          if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
          setMenusComplets(Array.isArray(data) ? data : []);
        })
        .catch(e => {
          setMenusErreur(e.message);
          setMenusComplets([]);
        });
    }
    // eslint-disable-next-line
  }, [onglet]);

  async function charger() {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/public/contrat/${token}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setContrat(data);
      setSigned(data.statut === 'accepte');
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }

  function showFlash(m, type = 'success') {
    setMsg({ text: m, type });
    setTimeout(() => setMsg(''), 5000);
  }

  async function envoyerCommentaire(e) {
    e.preventDefault();
    if (!msgTexte.trim() || !auteurNom.trim()) return;
    try {
      setEnvoi(true);
      const res = await fetch(`${API}/api/public/contrat/${token}/commentaires`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msgTexte, auteur_nom: auteurNom }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showFlash('Message envoyé ✓');
      setMsgTexte('');
      await charger();
    } catch (e) {
      showFlash('Erreur : ' + e.message, 'error');
    } finally {
      setEnvoi(false);
    }
  }

  async function accepterContrat() {
    if (!signeNom.trim()) { showFlash('Veuillez saisir votre nom complet.', 'error'); return; }
    if (!termsLus) { showFlash('Veuillez accepter les termes et conditions.', 'error'); return; }
    try {
      setSignEnvoi(true);
      const res = await fetch(`${API}/api/public/contrat/${token}/accepter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signe_par_nom: signeNom, signe_par_titre: signeTitre }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showFlash('Contrat accepté et signé avec succès ✓');
      setSigned(true);
      await charger();
    } catch (e) {
      showFlash('Erreur : ' + e.message, 'error');
    } finally {
      setSignEnvoi(false);
    }
  }

  async function refuserContrat() {
    try {
      const res = await fetch(`${API}/api/public/contrat/${token}/refuser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ raison: raisonRefus }),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showFlash('Contrat refusé.');
      setShowRefus(false);
      await charger();
    } catch (e) {
      showFlash('Erreur : ' + e.message, 'error');
    }
  }

  async function payer(type) {
    try {
      setPayEnvoi(true);
      const res = await fetch(`${API}/api/public/contrat/${token}/payer/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error((await res.json()).error);
      const { url } = await res.json();
      window.location.href = url;
    } catch (e) {
      showFlash('Erreur paiement : ' + e.message, 'error');
      setPayEnvoi(false);
    }
  }

  const LogoEscamel = ({ height = 60 }) => settings.logo_url
    ? <img src={settings.logo_url} alt="Escamel" style={{ height, objectFit: 'contain' }} />
    : <div className="pc-logo" style={{ fontSize: Math.round(height * 0.38) + 'px' }}>ESCAMEL</div>;

  if (loading) return (
    <div className="pc-loading">
      <LogoEscamel height={72} />
      <div className="pc-spinner" />
      <div className="pc-muted">Chargement…</div>
    </div>
  );

  if (erreur) return (
    <div className="pc-error">
      <LogoEscamel height={72} />
      <div className="pc-error-msg">{erreur}</div>
    </div>
  );

  if (!contrat) return null;

  // Brouillon non visible
  if (contrat.statut === 'brouillon') return (
    <div className="pc-error">
      <LogoEscamel height={72} />
      <div className="pc-error-msg">Cette page n'est pas encore disponible.</div>
    </div>
  );

  const { client, items = [], commentaires = [], termes } = contrat;
  const menuItems = items.filter(i => i.visible_menu && !i.est_rabais);
  const canSign = ['envoye', 'en_revision'].includes(contrat.statut);

  // Initialiser choixMenus depuis la DB si pas encore fait

  async function sauvegarderChoix(sectionId) {
    const choixSection = choixMenus[sectionId] || {};
    const choixArray = Object.entries(choixSection).map(([item_id, qte]) => ({
      item_id: Number(item_id), quantite_client: Number(qte) || 0,
    }));
    try {
      setSavingChoix(true);
      const res = await fetch(`${API}/api/public/contrat/${token}/menu-choix`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_id: sectionId, choix: choixArray }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erreur serveur');
      }
      showFlash('Choix sauvegardé ✓');
    } catch (e) {
      showFlash('Erreur : ' + e.message, 'error');
    } finally {
      setSavingChoix(false);
    }
  }

  async function toggleMenuDetail(itemId) {
    const newSet = new Set(expandedMenuItems);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
      setExpandedMenuItems(newSet);
      return;
    }
    newSet.add(itemId);
    setExpandedMenuItems(newSet);
    if (!menuDetailsCache[itemId]) {
      try {
        const res = await fetch(`${API}/api/public/contrat/${token}/menus`);
        const allMenus = await res.json();
        if (!res.ok) throw new Error(allMenus?.error || `HTTP ${res.status}`);
        // Trouver le menu lié à cet item de soumission
        const menuObj = (contrat.menus || []).find(m => Number(m.item_soumission_id) === Number(itemId));
        let sections;
        if (menuObj) {
          const found = allMenus.find(m => m.id === menuObj.id);
          sections = found?.sections || [];
        } else {
          // Fallback : afficher toutes les sections de tous les menus du contrat
          sections = allMenus.flatMap(m => m.sections || []);
        }
        setMenuDetailsCache(c => ({ ...c, [itemId]: sections }));
      } catch {
        setMenuDetailsCache(c => ({ ...c, [itemId]: [] }));
      }
    }
  }

  // Page d'accueil / landing
  if (showLanding) return (
    <div className="pc-page">
      {msg && <div className={`pc-flash ${msg.type === 'error' ? 'pc-flash-error' : 'pc-flash-success'}`}>{msg.text}</div>}
      <div className="pc-landing">
        {/* Logos */}
        <div className="pc-landing-logos">
          {client.logo_client_url && (
            <img src={client.logo_client_url} alt={client.entreprise} className="pc-landing-logo-client" />
          )}
          <div className="pc-landing-logo-escamel">
            <LogoEscamel height={80} />
          </div>
        </div>

        <div className="pc-landing-divider" />

        <h1 className="pc-landing-title">Bienvenue, {client.entreprise} !</h1>
        <p className="pc-landing-subtitle">Votre événement BBQ est en préparation.<br/>
          Escamel Artisans du Fumoir prépare une expérience gastronomique sur mesure pour votre équipe.
        </p>

        <div className="pc-landing-divider" />

        <h2 className="pc-landing-section-title">COMMENT ÇA FONCTIONNE</h2>
        <div className="pc-landing-steps">
          {[
            ['1', 'Consultez votre soumission personnalisée', 'Détails, menu et tarifs préparés pour vous'],
            ['2', 'Échangez avec notre équipe', 'Questions ou modifications ? On est là.'],
            ['3', 'Acceptez et signez en ligne', 'Signature électronique sécurisée'],
            ['4', `Payez votre acompte (${contrat.acompte_pct}%)`, 'Pour confirmer votre date'],
            ['5', 'Profitez du BBQ !', 'Notre équipe s\'occupe de tout le reste'],
          ].map(([icon, titre, desc]) => (
            <div key={titre} className="pc-landing-step">
              <span className="pc-step-icon">{icon}</span>
              <div>
                <div className="pc-step-titre">{titre}</div>
                <div className="pc-step-desc">{desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="pc-landing-divider" />

        <div className="pc-landing-event">
          <div className="pc-landing-event-item">
            <Icon name="date" size={14} color="var(--accent)" />
            {fmtDate(contrat.date_evenement)}
          </div>
          {contrat.lieu && (
            <div className="pc-landing-event-item">
              <Icon name="lieu" size={14} color="var(--accent)" />
              {contrat.lieu}
            </div>
          )}
          <div className="pc-landing-event-item">
            <Icon name="utilisateur" size={14} color="var(--accent)" />
            {contrat.nb_personnes} personnes
          </div>
        </div>

        <div className="pc-landing-statut">
          Statut : <span className={`pc-statut pc-statut-${contrat.statut}`}>{STATUT_LABELS[contrat.statut] || contrat.statut}</span>
        </div>

        <button className="pc-btn pc-btn-primary pc-btn-large" onClick={() => setShowLanding(false)}>
          Voir ma soumission →
        </button>
      </div>

      <footer className="pc-footer">
        <span>Escamel Artisans du Fumoir</span>
        <span>commandes@escamel.ca</span>
        <span>escamel.ca</span>
      </footer>
    </div>
  );

  return (
    <div className="pc-page">
      {/* Flash message */}
      {msg && (
        <div className={`pc-flash ${msg.type === 'error' ? 'pc-flash-error' : 'pc-flash-success'}`}>
          {msg.text}
        </div>
      )}

      {/* Header */}
      <header className="pc-header">
        <div className="pc-header-inner">
          <div className="pc-logo-area">
            <LogoEscamel height={64} />
          </div>
          {client.logo_client_url && (
            <img src={client.logo_client_url} alt={client.entreprise} className="pc-client-logo" />
          )}
        </div>
        <div className="pc-header-title">
          <div className="pc-doc-type">SOUMISSION CORPORATIVE</div>
          <div className="pc-doc-num">{contrat.numero_contrat}</div>
          <span className={`pc-statut pc-statut-${contrat.statut}`}>
            {STATUT_LABELS[contrat.statut] || contrat.statut}
          </span>
        </div>
      </header>

      {/* Détails événement */}
      <section className="pc-section pc-event-details">
        <div className="pc-event-grid">
          <div className="pc-event-item">
            <div className="pc-event-label">Date</div>
            <div className="pc-event-value">{fmtDate(contrat.date_evenement)}</div>
          </div>
          <div className="pc-event-item">
            <div className="pc-event-label">Lieu</div>
            <div className="pc-event-value">{contrat.lieu || '—'}</div>
          </div>
          <div className="pc-event-item">
            <div className="pc-event-label">Personnes</div>
            <div className="pc-event-value">{contrat.nb_personnes}</div>
          </div>
          {contrat.heure_service && (
            <div className="pc-event-item">
              <div className="pc-event-label">Heure service</div>
              <div className="pc-event-value">{contrat.heure_service}</div>
            </div>
          )}
          {contrat.adresse && (
            <div className="pc-event-item pc-event-full">
              <div className="pc-event-label">Adresse</div>
              <div className="pc-event-value">{contrat.adresse}</div>
            </div>
          )}
        </div>
      </section>

      {/* Navigation onglets */}
      <div className="pc-onglets">
        <button className={`pc-onglet ${onglet === 'soumission' ? 'active' : ''}`} onClick={() => setOnglet('soumission')}>Soumission</button>
        <button className={`pc-onglet ${onglet === 'menu' ? 'active' : ''}`} onClick={() => setOnglet('menu')}>Menu</button>
        <button className={`pc-onglet ${onglet === 'commentaires' ? 'active' : ''}`} onClick={() => setOnglet('commentaires')}>
          Messages
          {commentaires.filter(c => c.auteur !== 'client' && !c.lu_client).length > 0 && (
            <span className="pc-badge">{commentaires.filter(c => c.auteur !== 'client' && !c.lu_client).length}</span>
          )}
        </button>
        <button className={`pc-onglet ${onglet === 'paiements' ? 'active' : ''}`} onClick={() => setOnglet('paiements')}>Paiements</button>
        {canSign && !signed && (
          <button className={`pc-onglet ${onglet === 'signature' ? 'active' : ''}`} onClick={() => setOnglet('signature')}>
            Signer ✍
          </button>
        )}
      </div>

      {/* ─── Onglet Soumission ──────────────────────────────────────────────── */}
      {onglet === 'soumission' && (
        <section className="pc-section">
          <h2 className="pc-section-title">Détail de la soumission</h2>
          <div className="pc-items-table-wrapper">
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="pc-th-right">Qté</th>
                  <th className="pc-th-right">Prix unit.</th>
                  <th className="pc-th-right">Sous-total</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const isMenu     = item.categorie === 'menu';
                  const isExpanded = expandedMenuItems.has(item.id);
                  return (
                    <React.Fragment key={item.id}>
                      <tr className={`${item.est_rabais ? 'pc-tr-rabais' : ''} ${isMenu ? 'pc-tr-menu' : ''}`}>
                        <td>
                          {isMenu
                            ? <span className="pc-item-cat-menu">MENU</span>
                            : <div className="pc-item-cat">{item.categorie}</div>
                          }
                          <div className="pc-item-desc">
                          {isMenu && <Icon name="repas" size={13} color="var(--accent)" style={{ marginRight: 4 }} />}
                          {!!item.est_rabais && '↓ '}
                          {item.description}
                        </div>
                          {item.detail && <div className="pc-item-detail">{item.detail}</div>}
                          {isMenu && (
                            <button
                              className="pc-menu-toggle-btn"
                              onClick={() => toggleMenuDetail(item.id)}
                            >
                              {isExpanded ? '▲ Masquer le contenu du menu' : '▼ Voir le contenu du menu'}
                            </button>
                          )}
                        </td>
                        <td className="pc-td-right">{item.quantite} {item.unite}</td>
                        <td className="pc-td-right">{fmtMontant(item.prix_unitaire)}</td>
                        <td className={`pc-td-right ${item.est_rabais ? 'pc-rabais' : ''}`}>
                          {item.est_rabais ? '−' : ''}{fmtMontant(item.quantite * item.prix_unitaire)}
                        </td>
                      </tr>
                      {isMenu && isExpanded && (
                        <tr className="pc-tr-menu-detail">
                          <td colSpan={4} style={{ padding: 0 }}>
                            <div className="pc-menu-detail-block">
                              {!menuDetailsCache[item.id] ? (
                                <div className="pc-menu-detail-loading">Chargement…</div>
                              ) : menuDetailsCache[item.id].length === 0 ? (
                                <div className="pc-menu-detail-loading">Contenu du menu non défini</div>
                              ) : (
                                menuDetailsCache[item.id].map(sec => (
                                  <div key={sec.id} className="pc-menu-detail-section">
                                    <div className="pc-menu-detail-section-title">
                                      {sec.emoji} {sec.nom}
                                    </div>
                                    {(sec.items || []).map(it => {
                                      const nom = it.nom_override || it.cat_nom || '—';
                                      const desc = it.description_override || it.cat_description || '';
                                      const portion = it.quantite_par_personne
                                        ? `${it.quantite_par_personne} ${it.unite || 'portion'}/pers.`
                                        : '';
                                      return (
                                        <div key={it.id} className="pc-menu-detail-item">
                                          <span>{it.cat_emoji || '•'} {nom}</span>
                                          {portion && <span className="pc-menu-detail-portion">{portion}</span>}
                                          {desc && <div className="pc-menu-detail-desc">{desc}</div>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totaux */}
          <div className="pc-totaux">
            <div className="pc-total-ligne">
              <span>Sous-total</span>
              <span>{fmtMontant(contrat.sous_total)}</span>
            </div>
            {!client.tps_exempt && (
              <div className="pc-total-ligne">
                <span>TPS ({contrat.tps_pct}%)</span>
                <span>{fmtMontant(contrat.tps_montant)}</span>
              </div>
            )}
            {!client.tvq_exempt && (
              <div className="pc-total-ligne">
                <span>TVQ ({contrat.tvq_pct}%)</span>
                <span>{fmtMontant(contrat.tvq_montant)}</span>
              </div>
            )}
            <div className="pc-total-ligne pc-total-final">
              <span>TOTAL TTC</span>
              <span>{fmtMontant(contrat.total_ttc)}</span>
            </div>
          </div>

          {/* Notes client */}
          {contrat.notes_client && (
            <div className="pc-notes">
              <h3 className="pc-notes-title">Notes</h3>
              <p>{contrat.notes_client}</p>
            </div>
          )}

          {/* Signature visible */}
          {contrat.signe_le && (
            <div className="pc-signe-box">
              <Icon name="succes" size={15} weight="fill" />
              Accepté et signé par <strong>{contrat.signe_par_nom}</strong>
              {contrat.signe_par_titre && `, ${contrat.signe_par_titre}`}
              {' '}le {fmtDatetime(contrat.signe_le)}
            </div>
          )}
        </section>
      )}

      {/* ─── Onglet Menu ────────────────────────────────────────────────────── */}
      {onglet === 'menu' && (
        <section className="pc-section">
          <div className="pc-menu-header">
            <h2 className="pc-menu-title">Notre Menu</h2>
            <div className="pc-menu-subtitle">
              Préparé pour {client.entreprise} — {fmtDate(contrat.date_evenement)}
            </div>
            <div className="pc-menu-info">
              {contrat.nb_personnes} personnes
              {contrat.lieu && ` · ${contrat.lieu}`}
              {contrat.heure_service && ` · Service à ${contrat.heure_service}`}
            </div>
          </div>

          {/* ─ Nouveau système de menus (contrats_menus) ─ */}
          {menusErreur && (
            <p style={{ color: 'var(--danger)', padding: '12px 0' }}>Erreur : {menusErreur}</p>
          )}
          {menusComplets === null ? (
            <div className="pc-muted" style={{ textAlign: 'center', padding: '32px 0' }}>Chargement du menu…</div>
          ) : menusComplets.length > 0 ? menusComplets.map(menu => (
            <div key={menu.id} className="pc-menu-block">
              <h3 className="pc-menu-block-title">
                <Icon name="repas" size={18} color="var(--accent)" />
                {menu.nom}
                <span className="pc-menu-block-sub"> — {menu.nb_personnes || contrat.nb_personnes} personnes</span>
              </h3>
              {menu.description && <p className="pc-muted">{menu.description}</p>}

              {(menu.sections || []).map(sec => {
                const nbPersonnesSec = menu.nb_personnes || contrat.nb_personnes;
                const totalChoixSec = Object.values(choixMenus[sec.id] || {}).reduce((s, v) => s + (Number(v) || 0), 0);

                return (
                  <div key={sec.id} className="pc-menu-cat">
                    <h4 className="pc-menu-cat-title">
                      <span>{sec.emoji || ''}</span> {sec.nom}
                      {sec.type === 'choix' && <span className="pc-badge-choix">choix requis</span>}
                    </h4>
                    {sec.description && <p className="pc-muted" style={{ fontSize:13, marginBottom:8 }}>{sec.description}</p>}

                    {(sec.items || []).map(item => {
                      const nom = item.nom_override || item.cat_nom || '—';
                      const desc = item.description_override || item.cat_description || '';
                      const photo = item.photo_override || item.cat_photo;
                      const emoji = item.cat_emoji || '🍖';
                      const allergenes = item.cat_allergenes
                        ? (() => { try { return JSON.parse(item.cat_allergenes); } catch { return []; } })()
                        : [];

                      if (sec.type === 'fixe') {
                        return (
                          <div key={item.id} className="pc-menu-item pc-menu-item-photo">
                            {photo ? (
                              <img src={photo} alt={nom} className="pc-menu-item-img" />
                            ) : (
                              <div className="pc-menu-item-emoji">{emoji}</div>
                            )}
                            <div className="pc-menu-item-content">
                              <div className="pc-menu-item-name">{nom}</div>
                              {desc && <div className="pc-menu-item-desc">{desc}</div>}
                              {!!item.quantite_par_personne && (
                                <div className="pc-menu-item-portion">
                                  {item.quantite_par_personne}{item.unite} par personne
                                </div>
                              )}
                              {allergenes.length > 0 && (
                                <div className="pc-menu-item-allergenes">⚠ Contient : {allergenes.join(', ')}</div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      if (sec.type === 'choix' || sec.type === 'quantite') {
                        const qteClient = (choixMenus[sec.id] || {})[item.id] || 0;
                        return (
                          <div key={item.id} className="pc-choix-item">
                            {photo ? (
                              <img src={photo} alt={nom} className="pc-choix-img" />
                            ) : (
                              <span className="pc-choix-emoji">{emoji}</span>
                            )}
                            <div className="pc-choix-nom">{nom}</div>
                            <div className="pc-choix-input">
                              <input
                                type="number" min="0" max={nbPersonnesSec}
                                value={qteClient}
                                onChange={e => setChoixMenus(prev => ({
                                  ...prev,
                                  [sec.id]: { ...prev[sec.id], [item.id]: Number(e.target.value) || 0 },
                                }))}
                                className="pc-input-small"
                              />
                              <span className="pc-muted"> pers.</span>
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}

                    {/* Bouton sauvegarder pour les sections choix */}
                    {(sec.type === 'choix' || sec.type === 'quantite') && (
                      <div className="pc-choix-footer">
                        <div className={`pc-choix-total ${totalChoixSec === nbPersonnesSec ? 'pc-ok' : 'pc-warn'}`}>
                          Total : {totalChoixSec}/{nbPersonnesSec}
                          {totalChoixSec !== nbPersonnesSec && ` — doit égaler ${nbPersonnesSec}`}
                        </div>
                        <button
                          className="pc-btn pc-btn-primary"
                          onClick={() => sauvegarderChoix(sec.id)}
                          disabled={savingChoix || totalChoixSec !== nbPersonnesSec}
                        >
                          {savingChoix ? 'Sauvegarde…' : 'Sauvegarder mes choix'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )) : menuItems.length === 0 ? (
            <p className="pc-muted">Le menu n'a pas encore été configuré.</p>
          ) : (
            /* Fallback : ancien système (items directs) */
            Object.entries(
              menuItems.reduce((acc, item) => {
                if (!acc[item.categorie]) acc[item.categorie] = [];
                acc[item.categorie].push(item);
                return acc;
              }, {})
            ).map(([cat, catItems]) => (
              <div key={cat} className="pc-menu-cat">
                <h3 className="pc-menu-cat-title">{cat}</h3>
                {catItems.map(item => (
                  <div key={item.id} className="pc-menu-item">
                    <div className="pc-menu-item-name">{item.description}</div>
                    {item.detail && <div className="pc-menu-item-detail">{item.detail}</div>}
                  </div>
                ))}
              </div>
            ))
          )}

          {/* PDF menu */}
          <div className="pc-menu-pdf-link">
            <a href={`${API}/api/public/contrat/${token}/pdf-menu`} target="_blank" rel="noopener noreferrer" className="pc-pdf-link">
              <Icon name="telecharger" size={14} />
              Télécharger le menu employés PDF
            </a>
          </div>
        </section>
      )}

      {/* ─── Onglet Commentaires ────────────────────────────────────────────── */}
      {onglet === 'commentaires' && (
        <section className="pc-section">
          <h2 className="pc-section-title">Messages</h2>
          <div className="pc-messages">
            {commentaires.length === 0 && (
              <p className="pc-muted">Aucun message pour l'instant.</p>
            )}
            {commentaires.map(c => (
              <div key={c.id} className={`pc-message ${c.auteur === 'client' ? 'pc-msg-client' : 'pc-msg-escamel'}`}>
                <div className="pc-msg-header">
                  <span className="pc-msg-auteur">{c.auteur_nom || (c.auteur === 'client' ? 'Vous' : 'Escamel')}</span>
                  <span className="pc-msg-date">{fmtDatetime(c.cree_le)}</span>
                </div>
                <div className="pc-msg-texte">{c.message}</div>
              </div>
            ))}
          </div>

          <form onSubmit={envoyerCommentaire} className="pc-comment-form">
            <input
              type="text"
              placeholder="Votre nom complet"
              value={auteurNom}
              onChange={e => setAuteurNom(e.target.value)}
              required
              className="pc-input"
            />
            <textarea
              placeholder="Votre message, question ou modification demandée…"
              value={msgTexte}
              onChange={e => setMsgTexte(e.target.value)}
              rows={4}
              required
              className="pc-textarea"
            />
            <button type="submit" className="pc-btn pc-btn-primary" disabled={envoi}>
              {envoi ? 'Envoi…' : 'Envoyer le message'}
            </button>
          </form>
        </section>
      )}

      {/* ─── Onglet Paiements ───────────────────────────────────────────────── */}
      {onglet === 'paiements' && (
        <section className="pc-section">
          <h2 className="pc-section-title">Paiements</h2>
          <div className="pc-paiements">
            <div className={`pc-paiement-card ${contrat.acompte_paye ? 'pc-paye' : 'pc-card-unpaid'}`}>
              <div className="pc-paiement-label">Acompte ({contrat.acompte_pct}%)</div>
              <div className="pc-paiement-montant">
                {fmtMontant(
                  contrat.acompte_montant ||
                  ((contrat.total_ttc || contrat.solde_montant) * (contrat.acompte_pct || 0) / 100)
                )}
              </div>
              <div className={`pc-paiement-statut-badge ${contrat.acompte_paye ? 'pc-badge-paid' : 'pc-badge-unpaid'}`}>
                {contrat.acompte_paye
                  ? <><Icon name="succes" size={13} weight="fill" /> Payé le {fmtDate(contrat.acompte_date)}</>
                  : <><Icon name="heure" size={13} /> En attente de paiement</>}
              </div>
              {!contrat.acompte_paye && contrat.statut !== 'refuse' && (
                <button
                  className="pc-btn pc-btn-primary"
                  onClick={() => payer('acompte')}
                  disabled={payEnvoi}
                  style={{ marginTop: 12 }}
                >
                  {payEnvoi ? 'Redirection…' : (
                    <><Icon name="argent" size={15} weight="bold" /> Payer l'acompte en ligne</>
                  )}
                </button>
              )}
            </div>

            <div className={`pc-paiement-card ${contrat.solde_paye ? 'pc-paye' : ''}`}>
              <div className="pc-paiement-label">Solde restant</div>
              <div className="pc-paiement-montant">{fmtMontant(contrat.solde_montant)}</div>
              <div className={`pc-paiement-statut-badge ${contrat.solde_paye ? 'pc-badge-paid' : 'pc-badge-due'}`}>
                {contrat.solde_paye
                  ? <><Icon name="succes" size={13} weight="fill" /> Payé le {fmtDate(contrat.solde_date)}</>
                  : contrat.solde_due_date
                    ? <><Icon name="date" size={13} /> Dû avant le {fmtDate(contrat.solde_due_date)}</>
                    : <><Icon name="heure" size={13} /> En attente de paiement</>}
              </div>
              {!contrat.solde_paye && !!contrat.acompte_paye && contrat.statut !== 'refuse' && (
                <button
                  className="pc-btn pc-btn-primary"
                  onClick={() => payer('solde')}
                  disabled={payEnvoi}
                  style={{ marginTop: 12 }}
                >
                  {payEnvoi ? 'Redirection…' : (
                    <><Icon name="argent" size={15} weight="bold" /> Payer le solde en ligne</>
                  )}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ─── Onglet Signature ───────────────────────────────────────────────── */}
      {onglet === 'signature' && canSign && !signed && (
        <section className="pc-section">
          <h2 className="pc-section-title">Accepter la soumission</h2>

          {/* Termes */}
          {termes && (
            <div className="pc-termes-box">
              <h3 className="pc-termes-title">Termes et conditions</h3>
              <div className="pc-termes-contenu">
                {termes.contenu.split('\n').map((ligne, i) => {
                  if (ligne.startsWith('### ')) return <h5 key={i} style={{ color: 'var(--accent)', margin: '12px 0 4px' }}>{ligne.slice(4)}</h5>;
                  if (ligne.startsWith('## '))  return <h4 key={i} style={{ margin: '14px 0 4px' }}>{ligne.slice(3)}</h4>;
                  if (ligne.startsWith('# '))   return <h3 key={i} style={{ margin: '16px 0 6px' }}>{ligne.slice(2)}</h3>;
                  if (ligne.startsWith('- '))   return <li key={i} style={{ marginLeft: 18 }}>{ligne.slice(2)}</li>;
                  if (ligne === '')             return <br key={i} />;
                  return <p key={i} style={{ margin: '4px 0' }}>{ligne}</p>;
                })}
              </div>
              <label className="pc-checkbox-label">
                <input type="checkbox" checked={termsLus} onChange={e => setTermsLus(e.target.checked)} />
                J'ai lu et j'accepte les termes et conditions
              </label>
            </div>
          )}

          {/* Signature */}
          <div className="pc-signature-form">
            <div className="pc-input-group">
              <label>Nom complet du signataire *</label>
              <input
                type="text"
                className="pc-input"
                value={signeNom}
                onChange={e => setSigneNom(e.target.value)}
                placeholder="Prénom Nom"
              />
            </div>
            <div className="pc-input-group">
              <label>Titre (optionnel)</label>
              <input
                type="text"
                className="pc-input"
                value={signeTitre}
                onChange={e => setSigneTitre(e.target.value)}
                placeholder="Directeur, VP, etc."
              />
            </div>
          </div>

          <div className="pc-signature-actions">
            <button
              className="pc-btn pc-btn-primary pc-btn-large"
              onClick={accepterContrat}
              disabled={signEnvoi || !termsLus || !signeNom.trim()}
            >
              {signEnvoi ? 'Traitement…' : 'Accepter et signer le contrat'}
            </button>
            <button
              className="pc-btn pc-btn-danger"
              onClick={() => setShowRefus(true)}
            >
              Refuser la soumission
            </button>
          </div>
        </section>
      )}

      {/* Contrat déjà signé */}
      {signed && contrat.statut === 'accepte' && (
        <div className="pc-signed-banner">
          <Icon name="succes" size={16} weight="fill" />
          Ce contrat a été accepté et signé par <strong>{contrat.signe_par_nom}</strong>
          {' '}le {fmtDatetime(contrat.signe_le)}
        </div>
      )}

      {/* Modal refus */}
      {showRefus && (
        <div className="pc-modal-overlay" onClick={() => setShowRefus(false)}>
          <div className="pc-modal" onClick={e => e.stopPropagation()}>
            <h3>Refuser la soumission</h3>
            <p>Pouvez-vous nous indiquer la raison de votre refus ? Cela nous aidera à mieux vous servir.</p>
            <textarea
              className="pc-textarea"
              rows={4}
              placeholder="Raison du refus (optionnel)…"
              value={raisonRefus}
              onChange={e => setRaisonRefus(e.target.value)}
            />
            <div className="pc-modal-actions">
              <button className="pc-btn pc-btn-ghost" onClick={() => setShowRefus(false)}>Annuler</button>
              <button className="pc-btn pc-btn-danger" onClick={refuserContrat}>Confirmer le refus</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Links */}
      <div className="pc-pdf-section">
        <a href={`${API}/api/public/contrat/${token}/pdf-facture`} target="_blank" rel="noopener noreferrer" className="pc-pdf-link">
          <Icon name="document" size={14} />
          Télécharger la soumission PDF
        </a>
        <a href={`${API}/api/public/contrat/${token}/pdf-menu`} target="_blank" rel="noopener noreferrer" className="pc-pdf-link">
          <Icon name="repas" size={14} />
          Télécharger le menu PDF
        </a>
      </div>

      {/* Footer */}
      <footer className="pc-footer">
        <span>Escamel Artisans du Fumoir</span>
        <span>commandes@escamel.ca</span>
        <span>escamel.ca</span>
      </footer>
    </div>
  );
}
